import { FC, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useMutation, useQueryClient } from 'react-query'

import NetPromoterScoreAPI from '@api/netPromoterScoreAPI'
import { PrinterAPI } from '@api/printerAPI'
import QueueNumbersAPI from '@api/queueNumbersAPI'

import { QueueNodeModel } from '@models/queueNode'

import { Button } from '@components/Button'

import { ActionTypeEnum, useCustomerDisplay } from '@modules/customer-display/contexts/CustomerDisplayContext'
import { PermissionsControl } from '@modules/permissions'

import { PermissionEnum } from '@/constants/permission'
import { QUERIES } from '@/constants/queries'
import { useAlert, useToast } from '@/hooks'
import AxiosUtil from '@/utils/axiosUtil'
import TypeUtil from '@/utils/typeUtil'

import { QueueNodePatientNotes } from '../QueueNodePatientNotes/QueueNodePatientNotes'
import QueueNodePatientVisitType from '../QueueNodePatientVisitType/QueueNodePatientVisitType'
import { QueueNodePreferredCounterBadge } from '../QueueNodePreferredCounterBadge'
import { QueueNodePriorityBadge } from '../QueueNodePriorityBadge'
import { QueueProcessCalledActionButton } from '../QueueProcessCalledActionButton'

type Props = {
  currentNode: QueueNodeModel | undefined
  hasNextQueue: boolean
  onEdit: (node: QueueNodeModel) => void
  onComplete: (node: QueueNodeModel) => void
}

const QueueProcessCalled: FC<Props> = ({ currentNode, hasNextQueue, onEdit, onComplete }) => {
  const intl = useIntl()
  const alert = useAlert()
  const toast = useToast()
  const customerDisplay = useCustomerDisplay()
  const client = useQueryClient()
  const hasNotes =
    !!currentNode?.patient_visit_note ||
    currentNode?.patient_medical_note ||
    (currentNode?.patient_medicine_allergies && !TypeUtil.isEmpty(currentNode?.patient_medicine_allergies))

  const { mutate: recall, isLoading: isLoadingRecall } = useMutation({
    mutationFn: (node: QueueNodeModel) =>
      QueueNumbersAPI.updateStatus('repeat', {
        queue_number_id: node.queue_number_id,
      }),
    onSuccess: (_, { number }) => {
      toast.success(intl.formatMessage({ id: 'queue_node.recall.toast' }, { number: number }))
    },
    onError: (err) => {
      if (AxiosUtil.isAxiosError(err) && err.response?.data.message) {
        toast.error(err.response.data.message)
      }
      console.error(err)
    },
  })

  const { mutate: skip, isLoading: isLoadingSkip } = useMutation({
    mutationFn: (node: QueueNodeModel) =>
      QueueNumbersAPI.updateStatus('skip', {
        queue_number_id: node.queue_number_id,
      }),
    onSuccess: (_, { number }) => {
      client.invalidateQueries(QUERIES.QUEUE_PROCESS)
      alert.success({
        text: intl.formatMessage({ id: 'queue_node.skip.success' }, { number: number }),
      })
    },
    onError: (err) => {
      if (AxiosUtil.isAxiosError(err)) {
        alert.error({ text: err.response?.data.message })
      }
      console.error(err)
    },
  })

  const { mutate: standBy, isLoading: isLoadingStandBy } = useMutation({
    mutationFn: (node: QueueNodeModel) =>
      QueueNumbersAPI.updateStatus('stand-by', {
        queue_number_id: node.queue_number_id,
      }),
    onSuccess: (_, { number }) => {
      client.invalidateQueries(QUERIES.QUEUE_PROCESS)
      alert.success({
        text: intl.formatMessage({ id: 'queue_node.standby.success' }, { number: number }),
      })
    },
    onError: (err) => {
      if (AxiosUtil.isAxiosError(err)) {
        alert.error({ text: err.response?.data.message })
      }
      console.error(err)
    },
  })

  const { mutate: reprint, isLoading: isLoadingReprint } = useMutation({
    mutationFn: async (node: QueueNodeModel) => {
      const { data } = await QueueNumbersAPI.reprint({
        queue_number: node.number ? node.number : '',
      })
      if (!data?.signature || !data?.printer_data) {
        throw new Error('signature and printer data not found')
      }
      return PrinterAPI.print(data.signature, data.printer_data)
    },
    onSuccess: () => {
      toast.success(
        intl.formatMessage({
          id: 'queue_number.print.success',
        })
      )
    },
    onError: (err) => {
      if (AxiosUtil.isAxiosError(err) && err.response?.data.message) {
        toast.error(err.response.data.message)
      } else {
        toast.error(
          intl.formatMessage({
            id: 'queue_number.print.failed',
          })
        )
      }
    },
  })

  const { mutate: call, isLoading: isLoadingCall } = useMutation({
    mutationFn: () => QueueNumbersAPI.updateStatus('call'),
    onSuccess: () => {
      client.invalidateQueries(QUERIES.QUEUE_PROCESS)
      client.invalidateQueries(QUERIES.QUEUE_NUMBER_CURRENT)
    },
    onError: (err) => {
      if (AxiosUtil.isAxiosError(err) && err.response?.data.message) {
        toast.error(err.response.data.message)
      }
      console.error(err)
    },
  })

  const { mutate: rating, isLoading: isLoadingRating } = useMutation({
    mutationFn: () => NetPromoterScoreAPI.startByQueueNumber({ queue_number_id: currentNode?.queue_number_id! }),
    onSuccess: () => {
      customerDisplay.emit({ type: ActionTypeEnum.SHOW_NPS })
      alert.success({ text: intl.formatMessage({ id: 'nps.show_screen.success' }) })
    },
    onError: (err) => {
      if (AxiosUtil.isAxiosError(err)) {
        alert.error({ text: err.response?.data.message })
      } else {
        alert.error({ text: String(err) })
      }
    },
  })

  const handleRatingQueueNumber = async () => {
    const { isConfirmed } = await alert.question({
      text: intl.formatMessage({ id: 'nps.show_screen.confirmation' }),
      confirmButtonText: intl.formatMessage({ id: 'nps.show_screen.confirm' }),
      cancelButtonText: intl.formatMessage({ id: 'nps.show_screen.cancel' }),
    })

    if (isConfirmed) {
      rating()
    }
  }

  const handleStandbyQueueNumber = async (node: QueueNodeModel) => {
    const { isConfirmed } = await alert.question({
      text: intl.formatMessage({ id: 'queue_node.standby.text' }, { number: node.number }),
      confirmButtonText: intl.formatMessage({
        id: 'queue_node.standby.confirm',
      }),
    })

    if (isConfirmed) {
      standBy(node)
    }
  }

  const handleSkipQueueNumber = async (node: QueueNodeModel) => {
    const { isConfirmed } = await alert.question({
      text: intl.formatMessage({ id: 'queue_node.skip.text' }, { number: node.number }),
      confirmButtonText: intl.formatMessage({ id: 'queue_node.skip.confirm' }),
    })

    if (isConfirmed) {
      skip(node)
    }
  }

  useEffect(() => {
    client.invalidateQueries(QUERIES.QUEUE_NUMBER_CURRENT)
  }, [currentNode?.queue_number_id, client])

  return (
    <>
      <div className="text-muted">{intl.formatMessage({ id: 'queue.current.title' })}</div>

      <div className="d-flex">
        <div>
          <div className="d-flex gap-2 pt-2">
            {currentNode && currentNode.queue_counter_name && (
              <QueueNodePreferredCounterBadge counterName={currentNode.queue_counter_name} />
            )}

            {currentNode && currentNode.patient_visit_type && (
              <QueueNodePatientVisitType patientVisitType={currentNode.patient_visit_type} />
            )}
          </div>
          <div
            className="d-flex justify-content-start fw-medium"
            style={{ fontSize: '2.3rem' }}
          >
            {currentNode && (
              <div className="d-flex align-items-center me-3">
                {currentNode.priority && <QueueNodePriorityBadge priority={currentNode.priority} />}
              </div>
            )}

            {currentNode
              ? `${currentNode.number} - ${currentNode.patient_name ? currentNode.patient_name : currentNode.name}`
              : '-'}
          </div>
        </div>
        <div className="ms-auto">
          {hasNotes && (
            <QueueNodePatientNotes
              triageNotes={currentNode.patient_visit_note}
              medicalNotes={currentNode.patient_medical_note}
              medicineAllergies={currentNode.patient_medicine_allergies}
            />
          )}
        </div>
      </div>

      <div className="d-flex justify-content-start mt-3">
        {currentNode ? (
          <div className="d-flex flex-wrap w-100">
            <PermissionsControl allow={PermissionEnum.QUEUE_NUMBER_CALL_REPEAT}>
              <div className="d-grid col-4 p-1">
                <QueueProcessCalledActionButton
                  theme="info"
                  isLoading={isLoadingRecall}
                  onClick={() => recall(currentNode)}
                >
                  <div className="h-15px">
                    <i className="fa-solid fa-bullhorn" />
                  </div>
                  {intl.formatMessage({ id: 'queue_node.button.recall' })}
                </QueueProcessCalledActionButton>
              </div>
            </PermissionsControl>

            <PermissionsControl allow={PermissionEnum.QUEUE_NUMBER_SKIP}>
              <div className="d-grid col-4 p-1">
                <QueueProcessCalledActionButton
                  theme="danger"
                  isLoading={isLoadingSkip}
                  onClick={() => handleSkipQueueNumber(currentNode)}
                >
                  <div className="h-15px">
                    <i className="fa-solid fa-forward" />
                  </div>
                  {intl.formatMessage({ id: 'queue_node.button.skip' })}
                </QueueProcessCalledActionButton>
              </div>
            </PermissionsControl>

            <PermissionsControl allow={PermissionEnum.QUEUE_NUMBER_STAND_BY}>
              <div className="d-grid col-4 p-1">
                <QueueProcessCalledActionButton
                  theme="warning"
                  isLoading={isLoadingStandBy}
                  onClick={() => handleStandbyQueueNumber(currentNode)}
                >
                  <div className="h-15px">
                    <i className="fa-solid fa-street-view" />
                  </div>
                  {intl.formatMessage({ id: 'queue_node.button.standby' })}
                </QueueProcessCalledActionButton>
              </div>
            </PermissionsControl>

            <PermissionsControl allow={[PermissionEnum.QUEUE_NUMBER_UPDATE]}>
              <div className="d-grid col-4 p-1">
                <QueueProcessCalledActionButton
                  theme="secondary"
                  onClick={() => onEdit(currentNode)}
                >
                  <div className="h-15px">
                    <i className="fa-solid fa-pen" />
                  </div>
                  {intl.formatMessage({ id: 'queue_node.button.edit' })}
                </QueueProcessCalledActionButton>
              </div>
            </PermissionsControl>

            <PermissionsControl allow={PermissionEnum.QUEUE_NUMBER_COMPLETE}>
              <div className="d-grid col-4 p-1">
                <QueueProcessCalledActionButton
                  theme="success"
                  onClick={() => onComplete(currentNode)}
                >
                  <div className="h-15px">
                    <i className="fa-solid fa-check" />
                  </div>
                  {intl.formatMessage({ id: 'queue_node.button.complete' })}
                </QueueProcessCalledActionButton>
              </div>
            </PermissionsControl>

            <PermissionsControl allow={PermissionEnum.QUEUE_NUMBER_REPRINT}>
              <div className="d-grid col-4 p-1">
                <QueueProcessCalledActionButton
                  theme="secondary"
                  isLoading={isLoadingReprint}
                  onClick={() => reprint(currentNode)}
                >
                  <div className="h-15px">
                    <i className="fa-solid fa-print" />
                  </div>
                  {intl.formatMessage({ id: 'queue_node.button.reprint' })}
                </QueueProcessCalledActionButton>
              </div>
            </PermissionsControl>

            <PermissionsControl allow={PermissionEnum.NET_PROMOTER_SCORE_START_BY_QUEUE_NUMBER}>
              <div className="d-grid col-4 p-1">
                <QueueProcessCalledActionButton
                  theme="secondary"
                  isLoading={isLoadingRating}
                  onClick={handleRatingQueueNumber}
                >
                  <div className="h-15px">
                    <i className="fa-solid fa-ranking-star fs-5" />
                  </div>
                  {intl.formatMessage({ id: 'queue_node.button.rating' })}
                </QueueProcessCalledActionButton>
              </div>
            </PermissionsControl>
          </div>
        ) : (
          <PermissionsControl allow={PermissionEnum.QUEUE_NUMBER_CALL}>
            <Button
              theme="primary"
              disabled={!hasNextQueue}
              isLoading={isLoadingCall}
              onClick={() => call()}
            >
              <i className="fa-solid fa-bullhorn" />
              {intl.formatMessage({ id: 'queue_node.button.call' })}
            </Button>
          </PermissionsControl>
        )}
      </div>
    </>
  )
}

export { QueueProcessCalled }
